import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  TextField,
  MenuItem,
  Typography,
  Avatar,
  Grid,
  InputAdornment,
  Paper,
  Stack,
  CircularProgress,
  IconButton,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  PersonAdd,
  CheckCircle,
  TrendingUp,
  Public as PublicIcon,
  Hub as HubIcon,
  ContentCopy as ContentCopyIcon,
  Layers as LayersIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import LeadListTable from '../components/leads/LeadListTable';
import { useLeads, useExportLeads, useDeleteLead, useMarketingAnalytics, useAutoSegments } from '../hooks/useLeads';
import { LeadStatus, LeadSource, LeadFilters, LeadType } from '../types/lead';

const LeadsPage: React.FC = () => {
  const navigate = useNavigate();
  
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<LeadStatus | ''>('');
  const [sourceFilter, setSourceFilter] = useState<string>('');
  const [leadTypeFilter, setLeadTypeFilter] = useState<string>('');
  const [regionFilter, setRegionFilter] = useState<string>('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  // Active Auto-Segment Tab
  const [activeSegmentTab, setActiveSegmentTab] = useState<string>('all');
  
  // Webhook Integration Modal
  const [webhookModalOpen, setWebhookModalOpen] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState(false);

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  
  // Sorting
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Debounce search term
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Handle Preset Segment Tabs
  const handleSegmentTabChange = (_: React.SyntheticEvent, newValue: string) => {
    setActiveSegmentTab(newValue);
    setPage(1);
    
    // Reset specific custom filters when picking a preset
    if (newValue === 'all') {
      setSourceFilter('');
      setLeadTypeFilter('');
      setStatusFilter('');
      setRegionFilter('');
    } else if (newValue === 'indiamart') {
      setSourceFilter(LeadSource.INDIAMART);
      setLeadTypeFilter('');
      setRegionFilter('');
    } else if (newValue === 'meta_ads') {
      setSourceFilter(LeadSource.FACEBOOK_ADS);
      setLeadTypeFilter('');
      setRegionFilter('');
    } else if (newValue === 'wholesalers') {
      setLeadTypeFilter(LeadType.WHOLESALER);
      setSourceFilter('');
      setRegionFilter('');
    } else if (newValue === 'qualified') {
      setStatusFilter(LeadStatus.QUALIFIED);
      setSourceFilter('');
      setLeadTypeFilter('');
      setRegionFilter('');
    } else if (newValue === 'north_zone') {
      setRegionFilter('North');
      setSourceFilter('');
      setLeadTypeFilter('');
    } else if (newValue === 'west_zone') {
      setRegionFilter('West');
      setSourceFilter('');
      setLeadTypeFilter('');
    } else if (newValue === 'south_zone') {
      setRegionFilter('South');
      setSourceFilter('');
      setLeadTypeFilter('');
    }
  };

  // Fetch marketing analytics
  const { data: analyticsData } = useMarketingAnalytics(30);
  const { data: autoSegmentsData } = useAutoSegments();

  // Fetch leads with filters
  const { data, isLoading, error, refetch } = useLeads({
    page,
    per_page: pageSize,
    search: debouncedSearch || undefined,
    status: statusFilter ? [statusFilter] : undefined,
    source: sourceFilter ? [sourceFilter] : undefined,
    lead_type: leadTypeFilter || undefined,
    region: regionFilter || undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    sort_by: sortBy,
    sort_order: sortOrder,
  });

  const leads = data?.items || [];
  const total = data?.total || 0;

  // Export leads mutation
  const exportLeads = useExportLeads();
  const deleteLead = useDeleteLead();

  const handleCreateLead = () => {
    navigate('/admin/leads/create');
  };

  const handleExportLeads = async () => {
    const filters: LeadFilters = {};
    if (debouncedSearch) filters.search = debouncedSearch;
    if (statusFilter) filters.status = [statusFilter];
    if (sourceFilter) filters.source = [sourceFilter as LeadSource];
    if (leadTypeFilter) filters.lead_type = leadTypeFilter;
    if (regionFilter) filters.region = regionFilter;
    if (dateFrom) filters.date_from = dateFrom;
    if (dateTo) filters.date_to = dateTo;
    
    await exportLeads.mutateAsync(filters);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setSourceFilter('');
    setLeadTypeFilter('');
    setRegionFilter('');
    setDateFrom('');
    setDateTo('');
    setActiveSegmentTab('all');
    setPage(1);
  };

  const handleCopyText = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopyFeedback(true);
  };

  // Channel Stats
  const indiamartCount = analyticsData?.leads_by_source?.indiamart || 0;
  const metaAdsCount = (analyticsData?.leads_by_source?.facebook_ads || 0) + (analyticsData?.leads_by_source?.social_media || 0) + (analyticsData?.leads_by_source?.instagram || 0);
  const totalLeads = analyticsData?.total_leads || total || 0;
  const newLeads7d = analyticsData?.new_leads_7d || 0;
  const conversionRate = analyticsData?.conversion_rate || 0;

  return (
    <Box sx={{ p: 3, bgcolor: '#F8FAFC', minHeight: '100vh' }}>
      {/* Top Banner with Brand Gradient & Executive Styling */}
      <Paper
        sx={{
          background: 'linear-gradient(135deg, #1E293B 0%, #0F172A 100%)',
          color: 'white',
          p: 3.5,
          borderRadius: 3,
          mb: 3,
          boxShadow: '0 10px 25px -5px rgba(15, 23, 42, 0.3)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
              <Typography variant="h4" fontWeight={800} sx={{ letterSpacing: -0.5 }}>
                Marketing & Lead Engine
              </Typography>
              <Chip
                label="Omnichannel Hub"
                size="small"
                sx={{
                  bgcolor: 'rgba(56, 189, 248, 0.2)',
                  color: '#38BDF8',
                  fontWeight: 700,
                  border: '1px solid rgba(56, 189, 248, 0.3)',
                }}
              />
            </Box>
            <Typography variant="body1" sx={{ color: '#94A3B8', maxWidth: 700 }}>
              Live lead generation tracking across IndiaMART, Meta / Facebook Ads, and Inbound channels with automated regional and buyer segmentation.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.5} flexWrap="wrap">
            <Button
              variant="outlined"
              startIcon={<HubIcon />}
              onClick={() => setWebhookModalOpen(true)}
              sx={{
                borderColor: 'rgba(255,255,255,0.25)',
                color: '#E2E8F0',
                bgcolor: 'rgba(255,255,255,0.05)',
                backdropFilter: 'blur(8px)',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  borderColor: '#38BDF8',
                  color: '#38BDF8',
                  bgcolor: 'rgba(56, 189, 248, 0.1)',
                },
              }}
            >
              Webhook Integrations
            </Button>
            <Button
              variant="outlined"
              startIcon={<UploadIcon />}
              onClick={() => navigate('/admin/leads/import')}
              sx={{
                borderColor: 'rgba(255,255,255,0.25)',
                color: '#E2E8F0',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  borderColor: 'white',
                  bgcolor: 'rgba(255,255,255,0.1)',
                },
              }}
            >
              Import Leads
            </Button>
            <Tooltip title="Export leads to CSV based on active filters">
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExportLeads}
                disabled={exportLeads.isPending}
                sx={{
                  borderColor: 'rgba(255,255,255,0.25)',
                  color: '#E2E8F0',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'rgba(255,255,255,0.1)',
                  },
                }}
              >
                {exportLeads.isPending ? 'Exporting...' : 'Export CSV'}
              </Button>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateLead}
              sx={{
                bgcolor: '#2563EB',
                color: 'white',
                fontWeight: 700,
                textTransform: 'none',
                px: 2.5,
                '&:hover': { bgcolor: '#1D4ED8' },
                boxShadow: '0 4px 14px rgba(37, 99, 235, 0.4)',
              }}
            >
              Add New Lead
            </Button>
          </Stack>
        </Box>

        {/* Marketing KPI Metric Cards */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(12px)',
                borderRadius: 2.5,
                p: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar sx={{ bgcolor: 'rgba(56, 189, 248, 0.15)', color: '#38BDF8', width: 44, height: 44 }}>
                  <PersonAdd fontSize="small" />
                </Avatar>
                <Box>
                  <Typography variant="h5" color="white" fontWeight={800} lineHeight={1.1}>
                    {totalLeads}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 600 }}>
                    Total Inbound Leads
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card
              sx={{
                bgcolor: 'rgba(255, 111, 0, 0.12)',
                border: '1px solid rgba(255, 111, 0, 0.3)',
                backdropFilter: 'blur(12px)',
                borderRadius: 2.5,
                p: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar sx={{ bgcolor: '#FF6F00', color: 'white', width: 44, height: 44, fontWeight: 800, fontSize: '0.85rem' }}>
                  IM
                </Avatar>
                <Box>
                  <Typography variant="h5" color="white" fontWeight={800} lineHeight={1.1}>
                    {indiamartCount}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#FFCC80', fontWeight: 600 }}>
                    IndiaMART Inquiries
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card
              sx={{
                bgcolor: 'rgba(24, 119, 242, 0.15)',
                border: '1px solid rgba(24, 119, 242, 0.3)',
                backdropFilter: 'blur(12px)',
                borderRadius: 2.5,
                p: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar sx={{ bgcolor: '#1877F2', color: 'white', width: 44, height: 44, fontWeight: 800, fontSize: '0.85rem' }}>
                  FB
                </Avatar>
                <Box>
                  <Typography variant="h5" color="white" fontWeight={800} lineHeight={1.1}>
                    {metaAdsCount}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#90CAF9', fontWeight: 600 }}>
                    Meta / FB Ads Leads
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card
              sx={{
                bgcolor: 'rgba(16, 185, 129, 0.12)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                backdropFilter: 'blur(12px)',
                borderRadius: 2.5,
                p: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar sx={{ bgcolor: 'rgba(16, 185, 129, 0.2)', color: '#34D399', width: 44, height: 44 }}>
                  <TrendingUp fontSize="small" />
                </Avatar>
                <Box>
                  <Typography variant="h5" color="white" fontWeight={800} lineHeight={1.1}>
                    {newLeads7d}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#A7F3D0', fontWeight: 600 }}>
                    New Leads (7 Days)
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card
              sx={{
                bgcolor: 'rgba(147, 51, 234, 0.12)',
                border: '1px solid rgba(147, 51, 234, 0.3)',
                backdropFilter: 'blur(12px)',
                borderRadius: 2.5,
                p: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar sx={{ bgcolor: 'rgba(147, 51, 234, 0.2)', color: '#C084FC', width: 44, height: 44 }}>
                  <CheckCircle fontSize="small" />
                </Avatar>
                <Box>
                  <Typography variant="h5" color="white" fontWeight={800} lineHeight={1.1}>
                    {conversionRate}%
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#E9D5FF', fontWeight: 600 }}>
                    Conversion Rate
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Auto Segmentation Bar */}
      <Paper
        sx={{
          borderRadius: 3,
          p: 1.5,
          mb: 3,
          bgcolor: 'white',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          border: '1px solid #E2E8F0',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, px: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LayersIcon color="primary" fontSize="small" />
            <Typography variant="subtitle2" fontWeight={700} color="#1E293B">
              Smart Auto-Segments & Views
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            Quickly isolate high-intent buyer pools
          </Typography>
        </Box>
        <Tabs
          value={activeSegmentTab}
          onChange={handleSegmentTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: '40px',
            '& .MuiTab-root': {
              minHeight: '40px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
              borderRadius: 2,
              mx: 0.5,
              py: 0.5,
              px: 2,
              color: '#64748B',
              '&.Mui-selected': {
                color: '#2563EB',
                bgcolor: '#EFF6FF',
                fontWeight: 700,
              },
            },
            '& .MuiTabs-indicator': {
              display: 'none',
            },
          }}
        >
          <Tab value="all" label={`All Leads (${totalLeads})`} />
          <Tab
            value="indiamart"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#FF6F00' }} />
                <span>IndiaMART ({indiamartCount})</span>
              </Box>
            }
          />
          <Tab
            value="meta_ads"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#1877F2' }} />
                <span>Meta / FB Ads ({metaAdsCount})</span>
              </Box>
            }
          />
          <Tab
            value="wholesalers"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#7C3AED' }} />
                <span>Wholesalers & Distributors</span>
              </Box>
            }
          />
          <Tab value="qualified" label="High Intent (Qualified)" />
          <Tab value="north_zone" label="North Zone" />
          <Tab value="west_zone" label="West Zone" />
          <Tab value="south_zone" label="South Zone" />
        </Tabs>
      </Paper>

      {/* Search & Deep Filters */}
      <Card sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid #E2E8F0' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3.5}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by lead name, phone, company, email, query..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="primary" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: '#F8FAFC',
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Channel / Source"
              value={sourceFilter}
              onChange={(e) => {
                setSourceFilter(e.target.value);
                setActiveSegmentTab('custom');
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            >
              <MenuItem value="">All Channels</MenuItem>
              <MenuItem value={LeadSource.INDIAMART}>IndiaMART</MenuItem>
              <MenuItem value={LeadSource.FACEBOOK_ADS}>Meta / Facebook Ads</MenuItem>
              <MenuItem value={LeadSource.INSTAGRAM}>Instagram</MenuItem>
              <MenuItem value={LeadSource.WEBSITE}>Website</MenuItem>
              <MenuItem value={LeadSource.REFERRAL}>Referral</MenuItem>
              <MenuItem value={LeadSource.MANUAL_ENTRY}>Manual Entry</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Buyer Type"
              value={leadTypeFilter}
              onChange={(e) => {
                setLeadTypeFilter(e.target.value);
                setActiveSegmentTab('custom');
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            >
              <MenuItem value="">All Buyer Types</MenuItem>
              <MenuItem value={LeadType.WHOLESALER}>Wholesaler</MenuItem>
              <MenuItem value={LeadType.DISTRIBUTOR}>Distributor</MenuItem>
              <MenuItem value={LeadType.RETAILER}>Retailer</MenuItem>
              <MenuItem value={LeadType.EPC_CONTRACTOR}>EPC Contractor</MenuItem>
              <MenuItem value={LeadType.CORPORATE}>Corporate</MenuItem>
              <MenuItem value={LeadType.INDIVIDUAL}>Individual</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Region / Zone"
              value={regionFilter}
              onChange={(e) => {
                setRegionFilter(e.target.value);
                setActiveSegmentTab('custom');
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            >
              <MenuItem value="">All Regions</MenuItem>
              <MenuItem value="North">North India</MenuItem>
              <MenuItem value="West">West India</MenuItem>
              <MenuItem value="South">South India</MenuItem>
              <MenuItem value="East">East India</MenuItem>
              <MenuItem value="Central">Central India</MenuItem>
              <MenuItem value="North-East">North-East</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={1.5}>
            <TextField
              select
              fullWidth
              size="small"
              label="Status"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as LeadStatus | '');
                setActiveSegmentTab('custom');
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value={LeadStatus.NEW}>New Inquiry</MenuItem>
              <MenuItem value={LeadStatus.CONTACTED}>Contacted</MenuItem>
              <MenuItem value={LeadStatus.QUALIFIED}>Qualified</MenuItem>
              <MenuItem value={LeadStatus.PROPOSAL_SENT}>Proposal Sent</MenuItem>
              <MenuItem value={LeadStatus.NEGOTIATION}>Negotiation</MenuItem>
              <MenuItem value={LeadStatus.CONVERTED}>Converted</MenuItem>
              <MenuItem value={LeadStatus.LOST}>Lost</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={1}>
            <Tooltip title="Refresh leads list">
              <IconButton
                onClick={() => refetch()}
                disabled={isLoading}
                sx={{
                  bgcolor: '#EFF6FF',
                  color: '#2563EB',
                  '&:hover': { bgcolor: '#DBEAFE' },
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>

        {/* Active Filters Display */}
        {(searchTerm || statusFilter || sourceFilter || leadTypeFilter || regionFilter || dateFrom || dateTo) && (
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #F1F5F9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Active Filters:
              </Typography>
              {searchTerm && (
                <Chip
                  label={`Search: "${searchTerm}"`}
                  size="small"
                  onDelete={() => setSearchTerm('')}
                  color="primary"
                  variant="outlined"
                />
              )}
              {sourceFilter && (
                <Chip
                  label={`Source: ${sourceFilter}`}
                  size="small"
                  onDelete={() => setSourceFilter('')}
                  sx={{ bgcolor: '#FFF3E0', color: '#E65100', fontWeight: 600 }}
                />
              )}
              {leadTypeFilter && (
                <Chip
                  label={`Type: ${leadTypeFilter}`}
                  size="small"
                  onDelete={() => setLeadTypeFilter('')}
                  sx={{ bgcolor: '#F3E5F5', color: '#7B1FA2', fontWeight: 600 }}
                />
              )}
              {regionFilter && (
                <Chip
                  label={`Region: ${regionFilter}`}
                  size="small"
                  onDelete={() => setRegionFilter('')}
                  sx={{ bgcolor: '#E3F2FD', color: '#1565C0', fontWeight: 600 }}
                />
              )}
              {statusFilter && (
                <Chip
                  label={`Status: ${statusFilter}`}
                  size="small"
                  onDelete={() => setStatusFilter('')}
                  color="primary"
                  variant="outlined"
                />
              )}
            </Box>
            <Button
              variant="text"
              size="small"
              onClick={handleClearFilters}
              startIcon={<FilterListIcon />}
              sx={{ textTransform: 'none', color: '#64748B' }}
            >
              Reset All Filters
            </Button>
          </Box>
        )}
      </Card>

      {/* Leads Data Table */}
      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 16px rgba(0,0,0,0.04)', border: '1px solid #E2E8F0', overflow: 'hidden' }}>
        {error ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Alert severity="error">Failed to load leads. Please check your network and try again.</Alert>
          </Box>
        ) : isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10 }}>
            <CircularProgress />
          </Box>
        ) : (
          <LeadListTable
            leads={leads}
            loading={isLoading}
            page={page - 1}
            pageSize={pageSize}
            totalCount={total}
            onPageChange={(newPage) => setPage(newPage + 1)}
            onPageSizeChange={(newSize) => {
              setPageSize(newSize);
              setPage(1);
            }}
            onSortChange={(newSortBy, newSortOrder) => {
              setSortBy(newSortBy);
              setSortOrder(newSortOrder);
              setPage(1);
            }}
            onView={(leadId) => navigate(`/admin/leads/${leadId}`)}
            onEdit={(leadId) => navigate(`/admin/leads/${leadId}/edit`)}
            onDelete={(leadId) => {
              if (window.confirm('Are you sure you want to delete this lead?')) {
                deleteLead.mutate(leadId);
              }
            }}
          />
        )}
      </Card>

      {/* Webhook Integrations Modal for Marketing Manager */}
      <Dialog
        open={webhookModalOpen}
        onClose={() => setWebhookModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, p: 1 },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <PublicIcon color="primary" />
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Lead Ingestion Webhook Endpoints
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Connect IndiaMART & Meta (Facebook Ads) to ingest leads into Dhanshika CRM automatically
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3}>
            {/* IndiaMART Integration Box */}
            <Paper sx={{ p: 2.5, bgcolor: '#FFF8E1', border: '1px solid #FFE082', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip label="IndiaMART Ingestion" sx={{ bgcolor: '#FF6F00', color: 'white', fontWeight: 700 }} size="small" />
                  <Typography variant="subtitle2" fontWeight={700}>
                    Push API / Webhook URL
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => handleCopyText(`${window.location.origin}/api/webhooks/indiamart`)}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Box>
              <TextField
                fullWidth
                size="small"
                value={`${window.location.origin}/api/webhooks/indiamart`}
                InputProps={{ readOnly: true }}
                sx={{ bgcolor: 'white', mb: 1.5 }}
              />
              <Typography variant="body2" sx={{ color: '#5D4037', fontSize: '0.8125rem' }}>
                Paste this Webhook URL in your IndiaMART Seller Panel (Lead Manager &rarr; CRM Integration &rarr; Push API).
                All buyer inquiries, requirements, location, and contact numbers will appear here instantly.
              </Typography>
            </Paper>

            {/* Meta / Facebook Ads Integration Box */}
            <Paper sx={{ p: 2.5, bgcolor: '#EBF5FF', border: '1px solid #BFDBFE', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip label="Meta / FB Lead Ads" sx={{ bgcolor: '#1877F2', color: 'white', fontWeight: 700 }} size="small" />
                  <Typography variant="subtitle2" fontWeight={700}>
                    Meta Webhook Callback URL
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => handleCopyText(`${window.location.origin}/api/webhooks/meta-ads`)}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Box>
              <TextField
                fullWidth
                size="small"
                value={`${window.location.origin}/api/webhooks/meta-ads`}
                InputProps={{ readOnly: true }}
                sx={{ bgcolor: 'white', mb: 1.5 }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary">
                  Meta Hub Verify Token:
                </Typography>
                <Chip
                  label="DHANSHIKA_META_LEADS_VERIFY_TOKEN_2026"
                  size="small"
                  onClick={() => handleCopyText('DHANSHIKA_META_LEADS_VERIFY_TOKEN_2026')}
                  sx={{ cursor: 'pointer', fontFamily: 'monospace', fontWeight: 600 }}
                />
              </Box>
              <Typography variant="body2" sx={{ color: '#1E3A8A', fontSize: '0.8125rem' }}>
                Configure in Meta for Developers &rarr; Webhooks &rarr; Page &rarr; `leadgen`. Dhanshika will automatically verify the challenge token and parse inbound lead forms.
              </Typography>
            </Paper>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setWebhookModalOpen(false)} variant="contained" sx={{ textTransform: 'none' }}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Copy feedback notification */}
      <Snackbar
        open={copyFeedback}
        autoHideDuration={2500}
        onClose={() => setCopyFeedback(false)}
        message="Copied to clipboard!"
      />
    </Box>
  );
};

export default LeadsPage;

